import { useCallback, useEffect, useMemo, useState } from 'react';

import cloneDeep from 'lodash-es/cloneDeep';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

import { usePreferences } from '@/hooks/usePreferences';
import type { AccountFilterView } from '@/shared/types/analytics';
import type { Preferences, JsonValue } from '@/shared/types/user';

import { filtersEqualGeneric, type FilterLike } from './sessionFilterUtils';

export interface AccountViewApi<TFilter = unknown> {
  views: AccountFilterView[];
  activeView?: AccountFilterView;
  activeViewId?: string;
  hasUnsavedChanges: boolean;
  isCheckingMatch: boolean;
  setActiveViewId: (id: string | undefined) => void;
  saveFilterView: (name: string, overwriteId?: string) => Promise<void>;
  loadFilterView: (viewId: string) => TFilter[] | null;
  deleteFilterView: (viewId: string) => Promise<void>;
}

export function useAccountFilterViews<TFilter extends FilterLike = FilterLike>(
  currentFilters: TFilter[]
): AccountViewApi<TFilter> {
  const { t } = useTranslation();
  const { accountUserPrefs, updateAccountUserPreferences } = usePreferences();
  const [activeViewId, setActiveViewId] = useState<string | undefined>();
  const [isCheckingMatch, setIsCheckingMatch] = useState(true);

  const container = useMemo(() => {
    return (
      (accountUserPrefs?.account_filter_views as { views?: AccountFilterView[] } | undefined) ?? {
        views: [],
      }
    );
  }, [accountUserPrefs?.account_filter_views]);

  const views = useMemo(() => container.views ?? [], [container.views]);

  const activeView = useMemo(
    () => views.find((view) => view.id === activeViewId),
    [views, activeViewId]
  );

  const hasUnsavedChanges = useMemo(() => {
    if (!activeViewId) {
      return Array.isArray(currentFilters) && currentFilters.length > 0;
    }

    const view = views.find((item) => item.id === activeViewId);
    if (!view) {
      return false;
    }

    return !filtersEqualGeneric(
      currentFilters ?? [],
      (view.filters as typeof currentFilters) ?? []
    );
  }, [activeViewId, currentFilters, views]);

  // Auto-detect active view based on matching filters
  useEffect(() => {
    setIsCheckingMatch(true);

    // If no filters, clear active view
    if (!currentFilters || currentFilters.length === 0) {
      setActiveViewId(undefined);
      setIsCheckingMatch(false);
      return;
    }

    // Find a saved view that matches current filters
    const matchingView = views.find((view) =>
      filtersEqualGeneric(currentFilters ?? [], (view.filters as TFilter[]) ?? [])
    );

    if (matchingView) {
      setActiveViewId(matchingView.id);
    } else {
      // No matching view found, clear active view
      setActiveViewId(undefined);
    }

    setIsCheckingMatch(false);
  }, [currentFilters, views]);

  const saveFilterView = useCallback(
    async (name: string, overwriteId?: string) => {
      const trimmedName = name?.trim();

      if (!trimmedName) {
        toast.error(t('brain_logs.filter_view_name_required'));
        return;
      }

      if (trimmedName.length > 50) {
        toast.error(t('brain_logs.filter_view_name_too_long'));
        return;
      }

      const filtersToPersist = cloneDeep(currentFilters) as unknown[];

      let next: AccountFilterView[];
      if (overwriteId) {
        next = views.map((view) =>
          view.id === overwriteId ? { ...view, name: trimmedName, filters: filtersToPersist } : view
        );
        toast.success(t('brain_logs.filter_view_updated', { 0: trimmedName }));
      } else {
        const newView: AccountFilterView = {
          id: uuidv4(),
          name: trimmedName,
          filters: filtersToPersist,
        };
        next = [...views, newView];
        toast.success(t('brain_logs.filter_view_saved', { 0: trimmedName }));
      }

      const updatedPrefs: Preferences = {
        ...(accountUserPrefs || {}),
        account_filter_views: {
          views: next.map(({ id, name: viewName, filters }) => ({
            id,
            name: viewName,
            // Persist deep-cloned, JSON-safe filter objects
            filters: cloneDeep(filters) as JsonValue[],
          })),
        },
      };

      updateAccountUserPreferences(updatedPrefs);
      // Note: activeViewId will be auto-set by the useEffect when prefs update
    },
    [accountUserPrefs, currentFilters, t, updateAccountUserPreferences, views]
  );

  const loadFilterView = useCallback(
    (viewId: string) => {
      const view = views.find((item) => item.id === viewId);
      if (!view) {
        return null;
      }
      // Set activeViewId immediately for UI feedback
      // The useEffect will maintain it based on filter matching
      setActiveViewId(viewId);
      return cloneDeep(view.filters) as TFilter[];
    },
    [views]
  );

  const deleteFilterView = useCallback(
    async (viewId: string) => {
      const view = views.find((item) => item.id === viewId);
      if (!view) {
        return;
      }

      const next = views.filter((item) => item.id !== viewId);

      const updatedPrefs: Preferences = {
        ...(accountUserPrefs || {}),
        account_filter_views: {
          views: next.map(({ id, name, filters }) => ({
            id,
            name,
            filters: cloneDeep(filters) as JsonValue[],
          })),
        },
      };

      updateAccountUserPreferences(updatedPrefs);

      toast.success(t('brain_logs.filter_view_deleted', { 0: view.name }));
      // Note: activeViewId will be auto-cleared by useEffect since filters won't match deleted view
    },
    [accountUserPrefs, t, updateAccountUserPreferences, views]
  );

  return {
    views,
    activeView,
    activeViewId,
    hasUnsavedChanges,
    isCheckingMatch,
    setActiveViewId,
    saveFilterView,
    loadFilterView,
    deleteFilterView,
  };
}
